<template>
  <div class="task-list">
    <h2>任务列表</h2>
    <div v-if="!loading && tasks && tasks.length > 0">
      <div class="total-profit-container">
        <span class="total-profit-label">账号：</span>
        <span class="total-profit-value">{{ accountName }}</span>
      </div>
      <table>
        <thead>
          <tr>
            <th>任务ID</th>
            <th>任务收益</th>
            <th>开始时间</th>
            <th>结束时间</th>
            <th>耗时</th>
            <th>截止时间</th>
            <th>剩余时间</th>
            <th>任务状态</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="task in tasks" :key="task.task_id" @click="goToTaskDetail(task.task_id)" class="clickable-row">
            <td>{{ task.task_id }}</td>
            <td>{{ task.task_profit }}元</td>
            <td>{{ task.start_date }}</td>
            <td>{{ task.end_date }}</td>
            <td>{{ task.task_duration + 1 }}天</td>
            <td>{{ calculateDeadline(task.created_at, task.task_total_time) }}</td>
            <td>{{ calculateRemainingTime(task.created_at, task.task_total_time) }}</td>
            <td
              :class="{ 'status-completed': task.status === '已完成', 'status-in-progress': task.status === '进行中', 'status-failed': task.status === '失败' }">
              {{ task.status }}{{ task.status === '进行中' ? ' (' + task.expiry_time_str
        + ')' : '' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else-if="loading" class="no-data">正在加载...</div>
    <div v-else class="no-data">暂无任务</div>
  </div>
</template>

<script>
import apiService from '../services/api.js'

export default {
  name: 'TaskList',
  data() {
    return {
      tasks: [],
      loading: false,
      accountName: this.$route.query.accountName || '未知账户',
      timer: null
    }
  },
  methods: {
    goToTaskDetail(taskId) {
      const task = this.tasks.find(t => t.task_id === taskId);
      if (task) {
        const remainingTime = this.calculateRemainingTime(task.created_at, task.task_total_time);
        this.$router.push({
          path: `/account/${this.$route.params.id}/task/${taskId}`,
          query: {
            taskProfit: task.task_profit,
            remainingTime: remainingTime,
            currentAmount: task.current_amount,
            status: task.status
          }
        });
      }
    },
    formatDate(dateTime) {
      if (!dateTime || dateTime.trim() === '') {
        return ''
      }
      return dateTime.split(' ')[0]
    },
    calculateDuration(startTime, endTime) {
      if (!endTime || endTime.trim() === '') {
        return ''
      }
      const start = new Date(startTime.split(' ')[0])
      const end = new Date(endTime.split(' ')[0])
      const diffTime = Math.abs(end - start)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      return `${diffDays}天`
    },
    calculateDeadline(createdAt, totalTime) {
      if (!createdAt || !totalTime) {
        return '--'
      }
      const startDate = new Date(createdAt)
      const deadlineDate = new Date(startDate.getTime() + totalTime * 24 * 60 * 60 * 1000)
      return deadlineDate.toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    },
    calculateRemainingTime(createdAt, totalTime) {
      if (!createdAt || !totalTime) {
        return '--'
      }
      const startDate = new Date(createdAt)
      const deadlineDate = new Date(startDate.getTime() + totalTime * 24 * 60 * 60 * 1000)
      
      // 对于非进行中的任务，使用结束时间计算固定的时间差
      if (this.tasks) {
        const task = this.tasks.find(t => t.created_at === createdAt)
        if (task && task.status === '失败') {
          return '--'
        }
        if (task && task.status === '已完成' && task.end_date) {
          const endDate = new Date(task.end_date)
          const timeDiff = deadlineDate - endDate
          
          if (timeDiff <= 0) {
            return '已过期'
          }
          
          const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24))
          const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
          const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60))
          const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000)
          
          return `${days}天${hours}小时${minutes}分${seconds}秒`
        }
      }
      
      // 对于进行中的任务，显示实时倒计时
      const now = new Date()
      const remainingTime = deadlineDate - now
      
      if (remainingTime <= 0) {
        return '已过期'
      }
      
      const days = Math.floor(remainingTime / (1000 * 60 * 60 * 24))
      const hours = Math.floor((remainingTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const minutes = Math.floor((remainingTime % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((remainingTime % (1000 * 60)) / 1000)
      
      return `${days}天${hours}小时${minutes}分${seconds}秒`
    },
    startTimer() {
      this.timer = setInterval(() => {
        if (this.tasks && this.tasks.length > 0) {
          this.$forceUpdate()
        }
      }, 1000)
    }
  },
  beforeUnmount() {
    if (this.timer) {
      clearInterval(this.timer)
    }
  },
  async created() {
    this.startTimer()
    this.loading = true
    try {
      const accountId = this.$route.params.id
      const response = await apiService.getAccountTasksList(accountId)
      this.tasks = response.data
      this.loading = false
    } catch (error) {
      this.startTimer()
      console.error('获取任务列表失败:', error)
      // 如果API调用失败，使用模拟数据
      this.tasks = [
        {
          task_id: 1,
          account_id: 1,
          reward: 50,
          start_time: '2023-12-01 09:00:00',
          end_time: '2023-12-17 10:30:00',
          status: '已完成'
        },
        {
          task_id: 2,
          account_id: 1,
          reward: 75,
          start_time: '2023-12-02 14:00:00',
          end_time: null,
          status: '正在进行'
        }
      ]
      this.loading = false
    }
  }
}
</script>

<style scoped>
.task-list {
  margin: 30px auto;
  max-width: 900px;
  padding: 0 20px;
}

h2 {
  color: #2c3e50;
  font-size: 24px;
  margin-bottom: 25px;
  font-weight: 600;
}

.account-name {
  font-size: 18px;
  font-weight: 700;
  color: #10b981;
  margin-bottom: 15px;
  text-align: right;
  padding: 8px 15px;
  background-color: #f0fdf4;
  border-radius: 6px;
  display: inline-block;
  float: right;
}

table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-top: 20px;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

th,
td {
  padding: 12px;
  text-align: center;
  border-bottom: 1px solid #ddd;
}

th {
  background: linear-gradient(180deg, #f1f5f9, #e2e8f0);
  font-weight: 700;
  color: #334155;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #e2e8f0;
}

.clickable-row {
  cursor: pointer;
  transition: all 0.3s ease;
}

.clickable-row:hover {
  background-color: #f1f5f9;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.status-completed {
  color: #10b981;
  font-weight: 600;
}

.status-in-progress {
  color: #3b82f6;
  font-weight: 600;
}

.status-failed {
  color: #ef4444;
  font-weight: 600;
}

td {
  color: #334155;
  font-size: 14px;
}

tr:last-child td {
  border-bottom: none;
}

.no-data {
  text-align: center;
  padding: 20px;
  color: #64748b;
  font-size: 14px;
}

.total-profit-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: -20px;
  margin-bottom: -10px;
  padding: 10px 15px;
  /* background-color: #f8fafc;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05); */
}

.total-profit-label {
  font-size: 16px;
  font-weight: 600;
  color: #475569;
  margin-right: 10px;
}

.total-profit-value {
  font-size: 18px;
  font-weight: 700;
  color: #10b981;
}
</style>